/**
 * EstablishmentAddForm and related helpers for the Cibles établissement category.
 *
 * Contains:
 *   - PlaceSearchSuggestion / PlaceDetails interfaces
 *   - countryFlag helper
 *   - ZoneTypeIcon / ZoneField — geo zone input components
 *   - STREET_TYPE_RE / ARTICLE_RE and street-parsing helpers
 *   - precisionLevel / PrecisionMeter / EtablissementZoneIcon — precision display
 *   - EstablishmentAddForm — Google Places–first add/edit dialog
 */
import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PublicIcon from '@mui/icons-material/Public';
import TerrainIcon from '@mui/icons-material/Terrain';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import ApartmentIcon from '@mui/icons-material/Apartment';
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage';
import HomeIcon from '@mui/icons-material/Home';
import LocalPostOfficeIcon from '@mui/icons-material/LocalPostOffice';
import PinDropIcon from '@mui/icons-material/PinDrop';
import { Trans, useTranslation } from 'react-i18next';
import { TARGET_LIMITS, type TargetKeyCheckResult, type TargetKeyVerdict } from '@traaker/shared';
import { api } from '../../services/api';
import { useBrand } from '../../contexts/BrandContext';
import { type TargetRow, type TagRegistry, tagNamesOf } from './CATEGORIES';
import { type GeoSuggestion, useZoneAutocomplete } from './useZoneAutocomplete';
import { ChipListEditor } from './CatalogueViews';

export type { GeoSuggestion };

// ---------------------------------------------------------------------------
// Types specific to EstablishmentAddForm
// ---------------------------------------------------------------------------

export interface PlaceSearchSuggestion {
  placeId: string;
  mainText: string;
  secondaryText: string;
}

export interface PlaceDetails {
  placeId: string;
  name: string;
  formattedAddress: string;
  phone?: string;
  website?: string;
  latitude?: number;
  longitude?: number;
  countryCode?: string;
}

// ---------------------------------------------------------------------------
// countryFlag helper
// ---------------------------------------------------------------------------

/** ISO-3166-1 alpha-2 → flag emoji (e.g. "FR" → "🇫🇷"). */
export function countryFlag(code?: string): string {
  if (!code || code.length !== 2) return '';
  return code.toUpperCase().replace(/./g, (c) =>
    String.fromCodePoint(c.charCodeAt(0) + 127397),
  );
}

// ---------------------------------------------------------------------------
// ZoneTypeIcon
// ---------------------------------------------------------------------------

/**
 * Icon for the zone granularity — based on `type` only.
 * Note: Geoapify populates `address` for ALL result types (it's just the
 * formatted display string), so we NEVER use the presence of `address` to
 * infer street-level granularity — `type` is the only reliable signal.
 */
export function ZoneTypeIcon({ zone, size = 14 }: { zone?: any; size?: number }) {
  const sx = { fontSize: size, flexShrink: 0, color: 'text.disabled' };
  const type: string = zone?.type ?? '';
  if (type === 'country')                        return <PublicIcon sx={sx} />;       // 🌍
  if (type === 'region')                         return <TerrainIcon sx={sx} />;      // ⛰️
  if (type === 'postcode')                       return <LocalPostOfficeIcon sx={sx} />; // 📫
  if (type === 'address' || type === 'building') return <HomeIcon sx={sx} />;         // 🏠
  if (type === 'city')                           return <LocationCityIcon sx={sx} />; // 🏙️ city skyline
  if (type === 'village')                        return <HolidayVillageIcon sx={sx} />; // 🏘️ houses
  if (type === 'locality' || type === 'district' || type === 'suburb') return <ApartmentIcon sx={sx} />; // 🏢 neighborhood
  if (type)                                      return <ApartmentIcon sx={sx} />;
  return <PinDropIcon sx={sx} />;
}

// ---------------------------------------------------------------------------
// ZoneField
// ---------------------------------------------------------------------------

export function ZoneField({
  value, suggestions, attribution, onChange, onSelect, onFallback, size,
}: {
  value: string;
  suggestions: GeoSuggestion[];
  attribution: string;
  onChange: (v: string) => void;
  onSelect: (s: GeoSuggestion) => void;
  onFallback: () => void;
  size?: 'small';
}) {
  const { t } = useTranslation();
  const show = suggestions.length > 0 && value.length >= TARGET_LIMITS.geoAutocompleteMinLength;
  const noRes = !show && value.length >= TARGET_LIMITS.geoAutocompleteMinLength;
  return (
    <Box sx={{ position: 'relative', flex: 1 }}>
      <TextField
        size={size ?? 'small'}
        fullWidth
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('cibles.zone.placeholder')}
        sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}
      />
      {show && (
        <Box sx={{
          position: 'absolute', top: '100%', left: 0, zIndex: 400, mt: 0.5,
          minWidth: 420,  // wide enough for long French addresses
          bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider',
          borderRadius: 1.5, boxShadow: '0 8px 24px rgba(0,0,0,.12)', maxHeight: 280, overflow: 'auto',
        }}>
          {suggestions.map((s, i) => (
            <Box
              key={i}
              onClick={() => onSelect(s)}
              sx={{ px: 1.5, py: 0.875, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1, '&:hover': { bgcolor: alpha('#000', 0.04) } }}
            >
              {/* Flag — 1rem so it's readable */}
              <Typography component="span" sx={{ fontSize: '1.1rem', lineHeight: 1, flexShrink: 0, minWidth: 20, textAlign: 'center' }}>
                {s.countryCode ? countryFlag(s.countryCode) : '🌐'}
              </Typography>
              {/* Zone type icon — 18px so city vs locality is visually distinct */}
              <ZoneTypeIcon zone={s} size={18} />
              {/* Label + type hint */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>{s.label}</Typography>
                <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>{s.type}</Typography>
              </Box>
            </Box>
          ))}
          <Box sx={{ px: 1.5, py: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.disabled">{attribution}</Typography>
          </Box>
        </Box>
      )}
      {noRes && (
        <Button size="small" variant="text" onClick={onFallback} sx={{ mt: 0.25, fontSize: '0.7rem', color: 'text.secondary' }}>
          {t('cibles.zone.use_raw', { value })}
        </Button>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Street-parsing helpers (mirrors compile.py logic)
// ---------------------------------------------------------------------------

const STREET_TYPE_RE = /^(rue|avenue|av\.?\s*|boulevard|blvd?\.?\s*|bd\.?\s*|impasse|allée|allee|chemin|place|square|passage|cours|quai|route|villa|résidence|residence|voie|ruelle|venelle|esplanade|promenade|parvis|sente|sentier|cité|cite|domaine|galerie|terrasse|lot\.?\s*|hameau|street|st\.?\s*|road|rd\.?\s*|drive|dr\.?\s*|lane|ln\.?\s*|court|ct\.?\s*|close|way|terrace|ter\.?\s*|crescent|cres\.?\s*|gardens?|grove|park|walk|hill|rise|mews|yard|green|common)\s+/i;
// `d'` is load-bearing and was missing until 2026-09-24: "Rue d'Alsace" read back as
// "d'Alsace" here while `_STREET_HEAD` (compile.py) — which does list `d'` — keyed it as
// "alsace", so the form named a key the matcher did not have.
const ARTICLE_RE = /^(du|de\s+la|de\s+l['']|d['']|des|de\s+|le\s+|la\s+|les\s+|l['']|au\s+|aux\s+|of\s+the\s+|of\s+|the\s+)/i;

/**
 * Strip house number, street-type keyword and leading article from a raw street string.
 *
 * Mirrors `_street_core` + `_STREET_HEAD` in compile.py, which are ONE anchored regex: the
 * article is eaten only when it trails a thoroughfare type. Hence the two-step here — a
 * street with no type ("12 Les Halles") keeps its article on both sides, where stripping it
 * unconditionally would print "Halles" for a key that is "les halles".
 */
function stripStreetType(s: string): string {
  const noNumber = s
    .replace(/^\d+\s*[A-Za-z]?\s+/, '')  // leading number ("35 ", "2B ")
    .replace(/\s+\d+[A-Za-z]?$/, '');     // trailing number (" 74")
  const noType = noNumber.replace(STREET_TYPE_RE, '');
  return (noType === noNumber ? noType : noType.replace(ARTICLE_RE, '')).trim();
}

/**
 * Parse a Google formattedAddress into { streetName, city }.
 * "35 Av. Marcel Dassault, 31500 Toulouse, France" → { streetName: "Marcel Dassault", city: "Toulouse" }
 * "Paris, IDF, France"                             → { streetName: "",                city: "Paris" }
 * "Rue du Marché aux Herbes 74, 1000 Bruxelles"   → { streetName: "Marché aux Herbes", city: "Bruxelles" }
 */
export function extractAddressParts(addr: string): { streetName: string; city: string } {
  const parts = addr.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return { streetName: '', city: '' };

  const firstIsStreet = /^\d/.test(parts[0]) || STREET_TYPE_RE.test(parts[0]);

  if (!firstIsStreet) {
    return { streetName: '', city: parts[0] };
  }

  const streetName = stripStreetType(parts[0]);

  let city = '';
  for (let i = 1; i < parts.length - 1; i++) {
    const stripped = parts[i].replace(/^\d{3,6}\s+/, '').trim();
    if (stripped && !/^\d/.test(stripped)) { city = stripped; break; }
  }
  if (!city && parts.length > 1) city = parts[1].replace(/^\d+\s*/, '').trim();

  return { streetName, city };
}

/** Convenience wrapper — just the city (used for zone-only auto-name). */
export function extractCityFromAddress(addr: string): string {
  return extractAddressParts(addr).city || extractAddressParts(addr).streetName;
}

// ---------------------------------------------------------------------------
// Precision helpers
// ---------------------------------------------------------------------------

/** Precision level for an établissement row (1–3). */
export function precisionLevel(row: TargetRow): 1 | 2 | 3 {
  const placeIdField = row.fields.find((f) => f.name === 'placeId');
  if (placeIdField?.value) return 3;
  const zone = row.fields.find((f) => f.name === 'zone')?.value;
  if (zone && ['address', 'postcode', 'locality'].includes(zone.type)) return 2;
  return 1;
}

/** Three-segment precision bar (green = filled, grey = empty). */
export function PrecisionMeter({ level }: { level: 1 | 2 | 3 }) {
  return (
    <Box sx={{ display: 'flex', gap: '3px' }}>
      {([1, 2, 3] as const).map((i) => (
        <Box key={i} sx={{ width: 22, height: 4, borderRadius: 0.5, bgcolor: i <= level ? '#059669' : '#E4E7E4' }} />
      ))}
    </Box>
  );
}

/** Zone icon colored by precision level (amber dashed-circle / green pin / green pin+check). */
export function EtablissementZoneIcon({ level }: { level: 1 | 2 | 3 }) {
  if (level === 1) {
    return (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#9A5B12" strokeWidth="1.4" aria-hidden="true" style={{ flexShrink: 0, display: 'block' }}>
        <circle cx="8" cy="8" r="5.5" strokeDasharray="2.4 2.4" />
        <circle cx="8" cy="8" r="1.5" fill="#9A5B12" stroke="none" />
      </svg>
    );
  }
  if (level === 3) {
    return (
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#059669" strokeWidth="1.4" aria-hidden="true" style={{ flexShrink: 0, display: 'block' }}>
        <path d="M8 14.3s4.6-4 4.6-7.4A4.6 4.6 0 0 0 3.4 6.9C3.4 10.3 8 14.3 8 14.3Z" />
        <path d="M6.1 6.9 7.4 8.2 10 5.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#059669" strokeWidth="1.4" aria-hidden="true" style={{ flexShrink: 0, display: 'block' }}>
      <path d="M8 14.3s4.6-4 4.6-7.4A4.6 4.6 0 0 0 3.4 6.9C3.4 10.3 8 14.3 8 14.3Z" />
      <circle cx="8" cy="6.9" r="1.7" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// EstablishmentAddForm — Google Places–first add dialog for établissement
// ---------------------------------------------------------------------------

export function EstablishmentAddForm({
  open, mode = 'add', initialRow, marques, onAdd, onClose, saving, zoneState, tagRegistry,
}: {
  open: boolean;
  mode?: 'add' | 'edit';
  initialRow?: TargetRow;
  marques: TargetRow[];
  onAdd: (data: { label: string; parentTargetId?: string; fields: { name: string; value: any }[]; tags?: string[] }) => void;
  onClose: () => void;
  saving: boolean;
  zoneState: {
    query: string; suggestions: GeoSuggestion[]; attribution: string; selected: GeoSuggestion | null;
    onQueryChange: (v: string) => void; onSelect: (s: GeoSuggestion) => void;
  };
  tagRegistry: TagRegistry;
}) {
  const { t } = useTranslation();
  // Read here rather than threaded through as a prop: the key check is the only caller and
  // both render sites already sit under the same active brand.
  const { activeBrand } = useBrand();
  const brandId = activeBrand?.brandId ?? '';
  const [parentId, setParentId] = useState('');
  const [aliases, setAliases] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [placeQuery, setPlaceQuery] = useState('');
  const [placeResults, setPlaceResults] = useState<PlaceSearchSuggestion[]>([]);
  const [selectedDetails, setSelectedDetails] = useState<PlaceDetails | null>(null);
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [manualName, setManualName] = useState('');
  const hasUserEditedName = useRef(false);
  // Local zone state for edit mode (pre-filled from existing row)
  const localZone = useZoneAutocomplete(300);
  const placeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isEdit = mode === 'edit';
  // For edit mode: use local zone state; for add: use shared zoneState from parent
  const activeZone = isEdit
    ? { query: localZone.query, suggestions: localZone.suggestions, attribution: '', selected: localZone.selected,
        onQueryChange: localZone.onQueryChange,
        onSelect: localZone.onSelect,
      }
    : zoneState;

  // Reset / pre-fill form state when dialog opens
  useEffect(() => {
    if (!open) return;
    hasUserEditedName.current = false;
    setVerdicts(new Map());
    setAliasDraft('');
    if (isEdit && initialRow) {
      // The saved overrides, so a reopened fiche shows the toggles the way they were left.
      // Server-found conflicts are NOT restored from here — they are recomputed, and a stale
      // one would keep a value muted after the other fiche that shared it was deleted.
      setKeyOff(new Set(
        initialRow.fields
          .filter((f) => f.notUnique && typeof f.value === 'string')
          .map((f) => rowKey(f.name, f.value)),
      ));
      setParentId(initialRow.parentTargetId ?? '');
      const existingName = initialRow.fields.find((f) => f.name === 'name')?.value ?? '';
      setManualName(typeof existingName === 'string' ? existingName : '');
      setAliases(initialRow.fields.filter((f) => f.name === 'alias').map((f) => String(f.value)).filter(Boolean));
      // Names, not ids — the editor speaks names end to end, and a retired id resolves to
      // nothing here exactly as it does in the list.
      setTags(tagNamesOf(initialRow, tagRegistry));
      const existingZone = initialRow.fields.find((f) => f.name === 'zone')?.value;
      if (existingZone?.label) {
        localZone.onSelect(existingZone as GeoSuggestion);
      } else {
        localZone.reset();
      }
      setPlaceQuery('');
      setPlaceResults([]);
      setSelectedDetails(null);
    } else {
      setParentId(marques.length === 1 ? marques[0].targetId : '');
      setPlaceQuery('');
      setPlaceResults([]);
      setSelectedDetails(null);
      setManualName('');
      setAliases([]);
      setTags([]);
      setKeyOff(new Set());
    }
  }, [open]);


  const onPlaceQueryChange = (v: string) => {
    setPlaceQuery(v);
    setSelectedDetails(null);
    if (placeDebounceRef.current) clearTimeout(placeDebounceRef.current);
    if (v.length < 3) { setPlaceResults([]); return; }
    placeDebounceRef.current = setTimeout(async () => {
      try {
        const data = await api.get(`/locations/place-search?q=${encodeURIComponent(v)}&lang=fr`) as { suggestions: PlaceSearchSuggestion[] };
        setPlaceResults(data.suggestions ?? []);
      } catch { setPlaceResults([]); }
    }, 300);
  };

  const onPlaceSelect = async (sug: PlaceSearchSuggestion) => {
    setPlaceQuery(sug.mainText);
    setPlaceResults([]);
    setFetchingDetails(true);
    try {
      const details = await api.get(`/locations/place-details?placeId=${encodeURIComponent(sug.placeId)}&lang=fr`) as PlaceDetails;
      setSelectedDetails(details);
      // Fill the zone field so the manual fallback section stays in sync.
      if (details.formattedAddress) {
        activeZone.onSelect({
          label: details.formattedAddress,
          type: 'address',
          countryCode: details.countryCode ?? '',
          latitude: details.latitude,
          longitude: details.longitude,
        });
      }
      // Auto-fill the name: Brand — StreetName, City (unless user already typed something)
      if (!hasUserEditedName.current) {
        const brandLabel = marques.find((m) => m.targetId === parentId)?.label ?? '';
        const { streetName, city } = extractAddressParts(details.formattedAddress || '');
        const suffix = streetName && city ? `${city} ${streetName}`
          : (city || streetName || details.name || '');
        setManualName(brandLabel && suffix ? `${brandLabel} — ${suffix}` : suffix);
      }
    } catch { /* fall through to manual input */ }
    finally { setFetchingDetails(false); }
  };

  // -------------------------------------------------------------------------
  // Keys — which values can say WHICH branch an answer is about (§11.2)
  // -------------------------------------------------------------------------

  /**
   * The verdict is asked of the server on every change and NEVER persisted: the index
   * re-decides at each load, so a stored "unique" goes stale the day a second fiche gets the
   * same switchboard number. Only the opposite persists — the user telling us a value is
   * shared with établissements Traaker has no fiche for (`TargetField.notUnique`) — because
   * that is the one thing the shards cannot show.
   */
  const rowKey = (field: string, value: string) => `${field}::${value}`;
  const [keyOff, setKeyOff] = useState<Set<string>>(new Set());
  const [verdicts, setVerdicts] = useState<Map<string, TargetKeyVerdict>>(new Map());
  /** `undefined` = not asked or not answered yet; the city rung's row waits for `true`. */
  const [cityPairUnique, setCityPairUnique] = useState<boolean | undefined>(undefined);
  const [checking, setChecking] = useState(false);
  const [aliasDraft, setAliasDraft] = useState('');
  const checkSeq = useRef(0);

  const nameValue = manualName.trim();
  // Phone and url are not editable in this form: they arrive with a Google place, or they are
  // carried over from the saved fiche. Shown anyway, because they are keys and the point of
  // the table is to say what will identify the branch.
  const phoneValue = selectedDetails?.phone
    ?? (isEdit ? String(initialRow?.fields.find((f) => f.name === 'phone')?.value ?? '') : '');
  const urlValue = selectedDetails?.website
    ?? (isEdit ? String(initialRow?.fields.find((f) => f.name === 'url')?.value ?? '') : '');

  const keyRows: { field: 'name' | 'alias' | 'phone' | 'url'; value: string; aliasIndex?: number }[] = [
    ...(nameValue ? [{ field: 'name' as const, value: nameValue }] : []),
    ...aliases.map((a, i) => ({ field: 'alias' as const, value: a, aliasIndex: i })),
    ...(phoneValue ? [{ field: 'phone' as const, value: phoneValue }] : []),
    ...(urlValue ? [{ field: 'url' as const, value: urlValue }] : []),
  ];

  const zoneForCheck = selectedDetails
    ? {
      label: selectedDetails.formattedAddress,
      type: 'address',
      countryCode: selectedDetails.countryCode ?? '',
      // Mirrors what the Places branch of `submit` writes — see the comment there for why a
      // Places zone carries a parsed `cityName` at all.
      cityName: extractAddressParts(selectedDetails.formattedAddress).city,
    }
    : (activeZone.selected ?? undefined);

  // The marque + lieu ladder (§11.1) is a recognition key too — the one that carries the fiche
  // when no unique value does — so the table has to show it. What identifies the branch is
  // never the street or the city alone: it is the PAIR, the marque's name and the place having
  // to appear in the same sentence. Both rungs are DERIVED (parent marque × the fiche's own
  // zone), never typed, hence read-only — "is this unique?" is not a question the user answers
  // here. Kept OUT of `keyRows`: that array is what `key-check` is asked about per value and
  // what `activeKeys` counts, and the count strip's `summary_none` text already names the pair
  // as the fallback for a fiche with zero unique keys — counting it would contradict that.
  //
  // The two rungs are gated differently, exactly as the matcher gates them:
  //   * rue  — shown whenever there is a street. Never suppressed: two branches on one street
  //     both match and both surface as `probable`, which is honest ambiguity.
  //   * ville — shown only where the city names ONE branch, the verdict `cityPairUnique`
  //     brings back from the same shard walk `_pair_collision_mask` reads at index load. A
  //     shared city is the normal case, so surfacing every branch of a city would drown the
  //     signal rather than describe it.
  //
  // Street source mirrors the compiler's precedence (`compile.py` `street_f`): the explicit
  // `address` field, then `zone.address`, then the first comma segment of an `address`-type
  // zone label (the Google Places path stores nothing else). `extractAddressParts` plays the
  // part of `_street_core` — its `streetName` is both the existence gate (empty for a
  // city-level zone, exactly where the compiler blanks `street_f`) and the DISPLAYED value:
  // the house number, the thoroughfare type and the leading article are not part of the key
  // ("Rue du Marché aux Herbes 74" → "Marché aux Herbes", `_STREET_HEAD` + the number strip),
  // so printing the whole line named a key the matcher does not have. Accents and case are
  // kept where the fold drops them: that is the form engines actually write, and the row is
  // read by a human, not hashed.
  const pairAddressLine = String(
    initialRow?.fields.find((f) => f.name === 'address')?.value
      || (zoneForCheck as { address?: string } | undefined)?.address
      || (zoneForCheck?.type === 'address' ? zoneForCheck.label : '')
      || '',
  ).split(',')[0].trim();
  const pairStreet = extractAddressParts(pairAddressLine).streetName;
  const parentMarque = marques.find((m) => m.targetId === parentId);
  const pairMarque = parentMarque?.label ?? '';
  const pairKey = pairStreet && pairMarque ? `${pairMarque} + ${pairStreet}` : '';
  // `zone.cityName` and nothing else — the sole field the compiler reads for the city half, so
  // any other source here would offer a rung that is never emitted.
  const pairCity = String((zoneForCheck as { cityName?: string } | undefined)?.cityName ?? '').trim();
  // The marque's SIGNAL values, not its label: `parent_aliases` is built from the parent's
  // compiled `name`/`alias` signals, so a label the user never typed as a name would ask about
  // a pair that does not exist.
  const pairMarqueAliases = (parentMarque?.fields ?? [])
    .filter((f) => (f.name === 'name' || f.name === 'alias') && typeof f.value === 'string' && f.value.trim())
    .map((f) => String(f.value));
  const cityPairAsked = !!(pairCity && pairMarqueAliases.length);
  // `cityPairUnique === true` and not `!== false`: an unanswered rung shows nothing. The
  // opposite default would flash a row for the 600 ms before the first answer and then retract
  // it on the very fiches the gate exists for.
  const pairRows: { labelKey: string; hintKey: string; value: string }[] = [
    ...(pairKey ? [{ labelKey: 'cibles.keys.field.pair', hintKey: 'cibles.keys.pair_hint', value: pairKey }] : []),
    ...(cityPairAsked && cityPairUnique === true
      ? [{ labelKey: 'cibles.keys.field.pair_city', hintKey: 'cibles.keys.pair_city_hint', value: `${pairMarque} + ${pairCity}` }]
      : []),
  ];
  // The country code is part of the signature, not just the values: it is what turns a
  // national-format phone into E.164, so picking the zone changes the answer for an
  // unchanged number.
  const checkSignature = JSON.stringify([
    keyRows.map((r) => [r.field, r.value]),
    zoneForCheck?.countryCode ?? '',
    pairCity,
    pairMarqueAliases,
  ]);

  useEffect(() => {
    if (!open || !brandId || (!keyRows.length && !cityPairAsked)) {
      setVerdicts(new Map()); setCityPairUnique(undefined); return;
    }
    const handle = setTimeout(async () => {
      const seq = ++checkSeq.current;
      setChecking(true);
      try {
        const res = await api.post(`/brands/${brandId}/targets/key-check`, {
          targetId: isEdit ? initialRow?.targetId : undefined,
          zone: zoneForCheck,
          values: keyRows.map((r) => ({ field: r.field, value: r.value })),
          ...(cityPairAsked ? { pairCity, pairMarqueAliases } : {}),
        }) as TargetKeyCheckResult;
        // Debouncing does not order the answers: a slow earlier request can land after a
        // newer one and repaint the table with verdicts for values no longer in it.
        if (seq === checkSeq.current) {
          setVerdicts(new Map(res.verdicts.map((v) => [rowKey(v.field, v.value), v])));
          setCityPairUnique(res.cityPairUnique);
        }
      } catch {
        // A failed check must not paint red ticks: an unanswered value renders as a key,
        // which is also what the save will do.
        //
        // The city rung is the opposite: it is HIDDEN until the server says the city names one
        // branch, because the row is a promise the matcher has to keep. Silence is the safe
        // side here, a row claiming a suppressed rung is not.
        if (seq === checkSeq.current) { setVerdicts(new Map()); setCityPairUnique(undefined); }
      } finally {
        if (seq === checkSeq.current) setChecking(false);
      }
    }, 600);
    return () => clearTimeout(handle);
  }, [open, brandId, checkSignature]);

  /**
   * `key` identifies this branch · `shared`/`brand` — the server found another carrier ·
   * `off` — the user said it is shared with branches we have no fiche for · `thin` — too
   * short (or unparseable) to compile to any signal.
   *
   * An unanswered value is `key` on purpose: showing every row as "not a key" for the 600 ms
   * before the first answer would read as a verdict.
   */
  const keyStateOf = (field: string, value: string): 'key' | 'shared' | 'brand' | 'off' | 'thin' => {
    const v = verdicts.get(rowKey(field, value));
    if (v && !v.compiles) return 'thin';
    if (v?.conflict === 'marque') return 'brand';
    if (v?.conflict === 'etablissement') return 'shared';
    return keyOff.has(rowKey(field, value)) ? 'off' : 'key';
  };
  /** Stored on the field so the compiler emits no signal for it. */
  const isNotUnique = (field: string, value: string) => {
    const s = keyStateOf(field, value);
    return s === 'shared' || s === 'brand' || s === 'off';
  };
  const activeKeys = keyRows.filter((r) => keyStateOf(r.field, r.value) === 'key');

  const keySummaryParts: string[] = [];
  if (activeKeys.some((r) => r.field === 'name')) keySummaryParts.push(t('cibles.keys.part.name'));
  const activeAliases = activeKeys.filter((r) => r.field === 'alias').length;
  if (activeAliases) keySummaryParts.push(t('cibles.keys.part.alias', { count: activeAliases }));
  if (activeKeys.some((r) => r.field === 'phone')) keySummaryParts.push(t('cibles.keys.part.phone'));
  if (activeKeys.some((r) => r.field === 'url')) keySummaryParts.push(t('cibles.keys.part.url'));

  const addAlias = (raw: string) => {
    const v = raw.trim().slice(0, 80);
    setAliasDraft('');
    if (!v || aliases.length >= TARGET_LIMITS.maxEtablissementAliases) return;
    if (aliases.some((a) => a.trim().toLowerCase() === v.toLowerCase())) return;
    setAliases([...aliases, v]);
  };

  const submit = () => {
    const fields: { name: string; value: any; notUnique?: boolean }[] = [];
    /**
     * `notUnique` travels with the value, not with the row index, and it is the ONLY half of
     * the key table that is saved: a conflict the server found is recomputed at every load,
     * but it is still sent so the compiler skips the signal on THIS save too — otherwise the
     * form would show a struck-through row and then write a signal anyway.
     */
    const keyField = (name: 'name' | 'alias' | 'phone' | 'url', value: any) =>
      isNotUnique(name, String(value)) ? { name, value, notUnique: true } : { name, value };
    // One row per alias — `alias` is a multi-value field (maxCount 3), so the writer expects
    // repeated entries, not a joined string.
    const aliasFields = aliases.map((a) => keyField('alias', a));
    // ALWAYS sent, even empty: `POST /targets` merges, so the only way to retract the last tag
    // is an empty array. Omitting the key would make the final `×` a silent no-op.
    const tagPayload = { tags };
    if (selectedDetails) {
      const zone: GeoSuggestion = {
        label: selectedDetails.formattedAddress,
        type: 'address',
        countryCode: selectedDetails.countryCode ?? '',
        latitude: selectedDetails.latitude,
        longitude: selectedDetails.longitude,
        // Parsed out of the formatted address (2026-09-23). `PlaceDetails` carries no locality,
        // so this branch used to write a zone with NO `cityName` — and `cityName` is the only
        // field `compile.py` reads for the city half, so every Places-created fiche silently
        // got no `marque + ville` rung at all. The same parse already names the fiche
        // ("Dessange — Bruxelles") on the zone branch, so the string is one the user sees.
        cityName: extractAddressParts(selectedDetails.formattedAddress).city || undefined,
      };
      fields.push({ name: 'zone', value: { ...zone, geocodeStatus: 'ok' } });
      // The name the user SEES wins over Google's `displayName` (2026-09-23). The Nom input is
      // pre-filled from the address and editable, and this branch used to write
      // `selectedDetails.name` unconditionally — so an edit made right there was discarded and
      // the monitored name was a string the user had visibly replaced. The key table above
      // would have been describing a value that never reached the index.
      const savedName = nameValue || selectedDetails.name;
      if (savedName) fields.push(keyField('name', savedName));
      if (selectedDetails.phone) fields.push(keyField('phone', selectedDetails.phone));
      if (selectedDetails.website) fields.push(keyField('url', selectedDetails.website));
      fields.push({ name: 'placeId', value: selectedDetails.placeId });
      fields.push(...aliasFields);
      onAdd({ label: savedName, parentTargetId: parentId || undefined, fields, ...tagPayload });
    } else if (activeZone.selected) {
      fields.push({ name: 'zone', value: { ...activeZone.selected, geocodeStatus: 'ok' } });
      if (nameValue) fields.push(keyField('name', nameValue));
      // In edit mode, carry over phone/url/placeId that weren't touched. phone and url go
      // back through `keyField`: they are rows in the key table, so a toggle the user just
      // flipped on a carried-over value has to reach the save.
      if (isEdit && initialRow) {
        for (const keep of ['phone', 'url'] as const) {
          const existing = initialRow.fields.find((f) => f.name === keep)?.value;
          if (existing) fields.push(keyField(keep, existing));
        }
        const placeId = initialRow.fields.find((f) => f.name === 'placeId')?.value;
        if (placeId) fields.push({ name: 'placeId', value: placeId });
      }
      // Auto-name: "{BrandName} — {City}" when no explicit name is typed
      const brandLabel = marques.find((m) => m.targetId === parentId)?.label ?? '';
      const city = activeZone.selected.cityName || extractCityFromAddress(activeZone.selected.label) || activeZone.selected.label;
      const autoLabel = brandLabel && city ? `${brandLabel} — ${city}` : city;
      fields.push(...aliasFields);
      onAdd({ label: manualName.trim() || autoLabel, parentTargetId: parentId || undefined, fields, ...tagPayload });
    }
  };

  const canSubmit = !saving && (!!selectedDetails || !!activeZone.selected);

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5 } }}
    >
      {/* Header */}
      <Box sx={{ px: 3, pt: 3, pb: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: '1.2rem', letterSpacing: '-0.01em' }}>
            {isEdit ? t('cibles.etablissement.edit_title') : t('cibles.add.etablissement')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25, fontSize: '0.8125rem' }}>
            {t('cibles.etablissement.add_subtitle')}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={saving}
          sx={{ mt: -0.5, mr: -0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.25 }}>
          <CloseIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 2.5, pb: 3, px: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* Brand select */}
        <Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 600, fontSize: '0.75rem' }}>
            {t('cibles.parent_column')}
          </Typography>
          <Select
            size="small"
            value={parentId}
            onChange={(e) => setParentId(e.target.value as string)}
            displayEmpty
            fullWidth
            renderValue={(value) => {
              const brand = marques.find((m) => m.targetId === value);
              if (!brand) return <Typography sx={{ color: 'text.disabled', fontSize: '0.8125rem' }}>{t('cibles.parent_select')}</Typography>;
              return (
                <Box sx={{ display: 'inline-flex', bgcolor: alpha('#059669', 0.1), color: '#0B6647', fontWeight: 700, fontSize: '0.8rem', px: 1, py: 0.3, borderRadius: 0.75 }}>
                  {brand.label}
                </Box>
              );
            }}
            sx={{ fontSize: '0.8125rem' }}
          >
            <MenuItem value="" sx={{ fontSize: '0.8125rem', color: 'text.disabled' }}>{t('cibles.parent_select')}</MenuItem>
            {marques.map((m) => (
              <MenuItem key={m.targetId} value={m.targetId} sx={{ fontSize: '0.8125rem' }}>{m.label}</MenuItem>
            ))}
          </Select>
        </Box>

        {/* Google place search */}
        <Box sx={{ position: 'relative' }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 600, fontSize: '0.75rem' }}>
            {t('cibles.place_search.label')}
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.disabled', fontWeight: 400, fontSize: '0.75rem' }}>
              {t('cibles.place_search.via')}
            </Typography>
          </Typography>
          <TextField
            size="small"
            fullWidth
            autoFocus
            value={placeQuery}
            onChange={(e) => onPlaceQueryChange(e.target.value)}
            placeholder={t('cibles.place_search.placeholder')}
            disabled={saving}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: fetchingDetails ? '#059669' : 'text.disabled' }} /></InputAdornment>,
              endAdornment: fetchingDetails ? <InputAdornment position="end"><CircularProgress size={14} sx={{ color: '#059669' }} /></InputAdornment> : (
                placeQuery ? <InputAdornment position="end"><Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem', whiteSpace: 'nowrap' }}>{t('cibles.place_search.via')}</Typography></InputAdornment> : null
              ),
            }}
            sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}
          />
          {/* Dropdown */}
          {placeResults.length > 0 && (
            <Box sx={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 500, mt: 0.5, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1.5, boxShadow: '0 8px 24px rgba(0,0,0,.12)', overflow: 'hidden' }}>
              {placeResults.map((sug, i) => (
                <Box
                  key={sug.placeId}
                  onClick={() => onPlaceSelect(sug)}
                  sx={{
                    px: 1.5, py: 1.125, cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 1.25,
                    bgcolor: i === 0 ? alpha('#059669', 0.04) : 'transparent',
                    borderBottom: '1px solid', borderColor: alpha('#000', 0.04),
                    '&:last-child': { borderBottom: 'none' },
                    '&:hover': { bgcolor: alpha('#059669', 0.07) },
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke={i === 0 ? '#059669' : '#A0ABA4'} strokeWidth="1.4" aria-hidden="true" style={{ flexShrink: 0, marginTop: 2, display: 'block' }}>
                    <path d="M8 14.3s4.6-4 4.6-7.4A4.6 4.6 0 0 0 3.4 6.9C3.4 10.3 8 14.3 8 14.3Z" />
                    <circle cx="8" cy="6.9" r="1.7" />
                  </svg>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8125rem', fontWeight: i === 0 ? 600 : 500 }}>{sug.mainText}</Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{sug.secondaryText}</Typography>
                  </Box>
                  {i === 0 && (
                    <Box sx={{ bgcolor: alpha('#059669', 0.12), color: '#0B6647', fontSize: '0.65rem', fontWeight: 700, px: 0.75, py: 0.3, borderRadius: 0.5, flexShrink: 0, alignSelf: 'center' }}>
                      {t('cibles.place_search.enter_hint')}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Auto-fill preview */}
        {selectedDetails && (
          <Box sx={{ px: 1.75, py: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: alpha('#059669', 0.25), bgcolor: alpha('#059669', 0.035) }}>
            <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#0B6647', display: 'block', mb: 1 }}>
              {t('cibles.place_search.autofill_title')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.625 }}>
              {[
                t('cibles.col.etablissement.name'),
                ...(selectedDetails.phone ? [t('cibles.col.etablissement.phone')] : []),
                t('cibles.col.etablissement.zone'),
                ...(selectedDetails.website ? [t('cibles.col.etablissement.url')] : []),
                t('cibles.col.etablissement.placeId'),
              ].map((label) => (
                <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 13, color: '#059669', flexShrink: 0 }} />
                  <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{label}</Typography>
                </Box>
              ))}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <PrecisionMeter level={3} />
                <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#0B6647' }}>{t('cibles.precision.level3')} 3/3</Typography>
              </Box>
            </Box>
          </Box>
        )}

        {/* Manual fallback */}
        <Divider><Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>{t('cibles.place_search.fallback')}</Typography></Divider>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 600, fontSize: '0.75rem' }}>
              {t('cibles.col.etablissement.zone')}
            </Typography>
            <ZoneField
              value={activeZone.query}
              suggestions={activeZone.suggestions}
              attribution={activeZone.attribution}
              onChange={(v) => activeZone.onQueryChange(v)}
              onSelect={(s) => activeZone.onSelect(s)}
              onFallback={() => activeZone.onSelect({ label: activeZone.query, type: 'locality', countryCode: 'FR', cityName: activeZone.query })}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 600, fontSize: '0.75rem' }}>
              {t('cibles.col.etablissement.name')}
              {' '}
              <Typography component="span" variant="caption" sx={{ fontWeight: 400, color: 'text.disabled', fontSize: '0.75rem' }}>{t('cibles.optional')}</Typography>
            </Typography>
            <TextField
              size="small"
              fullWidth
              value={manualName}
              onChange={(e) => { hasUserEditedName.current = true; setManualName(e.target.value); }}
              placeholder={t('cibles.ph.etablissement.name')}
              disabled={saving}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem' } }}
            />
            <Typography variant="caption" sx={{ mt: 0.3, fontSize: '0.7rem', color: 'text.disabled', display: 'block' }}>
              {t('cibles.etablissement.auto_name_hint')}
            </Typography>
          </Box>
        </Box>

        {/* Street-level prompt.
            Not a hard block — a city-level cible still measures something, and refusing the
            save would strand anyone whose branch Google does not list. But brand + street is
            the ONE signal that separates two branches of the same marque in the same city, so
            a zone that stops at the city has to say out loud what it cannot do, at the moment
            the choice is made rather than as a warning on the row afterwards. */}
        {!selectedDetails && activeZone.selected && activeZone.selected.type !== 'address' && (
          <Box sx={{ display: 'flex', gap: 1.25, px: 1.75, py: 1.25, borderRadius: 1.5, bgcolor: '#FBF3E4', border: '1px solid #F0E4CC' }}>
            <Box sx={{ flexShrink: 0, color: '#7A4A10', fontSize: 14, lineHeight: 1.4 }}>★</Box>
            <Box>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, fontSize: '0.75rem', color: '#7A4A10' }}>
                {t('cibles.etablissement.street_needed')}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.72rem', color: '#7C5A1E', lineHeight: 1.4 }}>
                {t('cibles.etablissement.street_needed_hint')}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Keys — what can say WHICH branch an answer is about (§11.2).
            The aliases live INSIDE this table rather than in a chip box of their own: an alias
            only exists to be recognised, so showing it apart from the verdict on it was the
            whole problem — a user added "Dessange Bruxelles" to three salons and nothing told
            them the three cancel each other out. */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, fontSize: '0.75rem' }}>
                {t('cibles.keys.title')}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.2, fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.45 }}>
                {t('cibles.keys.subtitle')}
              </Typography>
            </Box>
            {checking && <CircularProgress size={13} sx={{ color: '#059669', mt: 0.3, flexShrink: 0 }} />}
          </Box>

          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', px: 1.25, py: 0.625, bgcolor: alpha('#000', 0.015), borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ flex: 1 }} />
              <Typography variant="caption" sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                {t('cibles.keys.column')}
              </Typography>
            </Box>

            {keyRows.length === 0 && !pairRows.length && (
              <Typography variant="caption" sx={{ display: 'block', px: 1.25, py: 1.25, fontSize: '0.73rem', color: 'text.disabled' }}>
                {t('cibles.keys.empty')}
              </Typography>
            )}

            {/* Derived, read-only: no toggle and no delete. Same green key icon as an active
                row — they ARE working keys — but the verdict cell says "automatique" instead of
                claiming uniqueness, which a street pair cannot. */}
            {pairRows.map((p) => (
              <Box key={p.labelKey} sx={{ px: 1.25, py: 0.75, borderBottom: '1px solid', borderColor: alpha('#000', 0.05), bgcolor: alpha('#000', 0.012) }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 14, flexShrink: 0, display: 'flex', color: '#059669' }}>
                    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                      <circle cx="5.4" cy="10.6" r="3" />
                      <path d="M7.5 8.5 13.2 2.8M11 5l1.7 1.7M9.4 6.6l1.3 1.3" />
                    </svg>
                  </Box>
                  <Typography variant="caption" sx={{ width: 62, flexShrink: 0, fontSize: '0.68rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                    {t(p.labelKey)}
                  </Typography>
                  <Typography sx={{ flex: 1, minWidth: 0, fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.value}
                  </Typography>
                  <Typography variant="caption" sx={{
                    flexShrink: 0, fontSize: '0.68rem', fontWeight: 600, whiteSpace: 'nowrap',
                    px: 0.75, py: 0.3, borderRadius: 0.75, border: '1px solid',
                    borderColor: 'divider', color: 'text.secondary',
                  }}>
                    {t('cibles.keys.pair_auto')}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ display: 'block', pl: '86px', mt: 0.25, fontSize: '0.68rem', color: 'text.secondary', lineHeight: 1.4 }}>
                  {t(p.hintKey)}
                </Typography>
              </Box>
            ))}

            {keyRows.map((r) => {
              const state = keyStateOf(r.field, r.value);
              const on = state === 'key';
              // A conflict and a value too thin to compile are FACTS, not preferences: the
              // toggle is inert there, because clicking "yes, unique" would not make it one.
              // The user's own "no" (`off`) stays reversible.
              const locked = state !== 'key' && state !== 'off';
              return (
                <Box key={`${r.field}:${r.aliasIndex ?? 0}:${r.value}`}
                  sx={{ px: 1.25, py: 0.75, borderBottom: '1px solid', borderColor: alpha('#000', 0.05) }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 14, flexShrink: 0, display: 'flex', color: on ? '#059669' : 'text.disabled', opacity: on ? 1 : 0.4 }}>
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                        <circle cx="5.4" cy="10.6" r="3" />
                        <path d="M7.5 8.5 13.2 2.8M11 5l1.7 1.7M9.4 6.6l1.3 1.3" />
                      </svg>
                    </Box>
                    <Typography variant="caption" sx={{ width: 62, flexShrink: 0, fontSize: '0.68rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                      {t(`cibles.keys.field.${r.field}`)}
                    </Typography>
                    <Typography sx={{ flex: 1, minWidth: 0, fontSize: '0.8125rem', fontWeight: on ? 600 : 400, color: on ? 'text.primary' : 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {r.value}
                    </Typography>
                    <Box
                      onClick={() => {
                        if (locked || saving) return;
                        setKeyOff((prev) => {
                          const next = new Set(prev);
                          const k = rowKey(r.field, r.value);
                          if (next.has(k)) next.delete(k); else next.add(k);
                          return next;
                        });
                      }}
                      sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5, flexShrink: 0,
                        px: 0.75, py: 0.3, borderRadius: 0.75, border: '1px solid',
                        borderColor: on ? alpha('#059669', 0.35) : 'divider',
                        bgcolor: on ? alpha('#059669', 0.08) : 'transparent',
                        cursor: locked || saving ? 'default' : 'pointer',
                        opacity: locked ? 0.5 : 1,
                      }}
                    >
                      <Box sx={{
                        width: 12, height: 12, borderRadius: 0.4, flexShrink: 0,
                        border: '1.5px solid', borderColor: on ? '#059669' : alpha('#000', 0.25),
                        bgcolor: on ? '#059669' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {on && (
                          <svg width="8" height="8" viewBox="0 0 10 10" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M1.5 5.2 3.8 7.5 8.5 2.5" />
                          </svg>
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ fontSize: '0.68rem', fontWeight: 600, whiteSpace: 'nowrap', color: on ? '#0B6647' : 'text.secondary' }}>
                        {t('cibles.keys.toggle_yes')}
                      </Typography>
                    </Box>
                    {r.aliasIndex !== undefined && (
                      <IconButton size="small" disabled={saving}
                        onClick={() => setAliases(aliases.filter((_, i) => i !== r.aliasIndex))}
                        sx={{ p: 0.125, flexShrink: 0, color: alpha('#000', 0.25), '&:hover': { color: '#C0392B', bgcolor: 'transparent' } }}>
                        <CloseIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    )}
                  </Box>
                  {state !== 'key' && (
                    <Typography variant="caption" sx={{ display: 'block', pl: '86px', mt: 0.25, fontSize: '0.68rem', color: '#8A6212', lineHeight: 1.4 }}>
                      {t(`cibles.keys.warn_${state}`)}
                    </Typography>
                  )}
                </Box>
              );
            })}

            {/* Add an alias — a row of the table, not a separate box, so the value and the
                verdict on it appear in the same place. */}
            {aliases.length < TARGET_LIMITS.maxEtablissementAliases && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.5 }}>
                <Box sx={{ width: 14, flexShrink: 0, display: 'flex', justifyContent: 'center', color: 'text.disabled' }}>
                  <AddIcon sx={{ fontSize: 13 }} />
                </Box>
                <TextField
                  variant="standard"
                  value={aliasDraft}
                  onChange={(e) => setAliasDraft(e.target.value.slice(0, 80))}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAlias(aliasDraft); } }}
                  onBlur={() => addAlias(aliasDraft)}
                  placeholder={t('cibles.keys.add_alias')}
                  disabled={saving}
                  InputProps={{ disableUnderline: true }}
                  sx={{ flex: 1, minWidth: 0, '& .MuiInputBase-input': { fontSize: '0.8125rem', py: 0.5 } }}
                />
                <Typography variant="caption" sx={{ flexShrink: 0, fontSize: '0.68rem', color: 'text.disabled', display: { xs: 'none', sm: 'block' } }}>
                  {t('cibles.keys.add_alias_hint')}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Count strip. Zero keys is the case worth saying out loud: the fiche still matches
              on marque + street (the pair route), which is a different, weaker thing. */}
          <Box sx={{
            mt: 1, display: 'flex', alignItems: 'center', gap: 0.875, px: 1.5, py: 1, borderRadius: 1.5,
            border: '1px solid',
            bgcolor: activeKeys.length ? alpha('#059669', 0.06) : '#FBF3E4',
            borderColor: activeKeys.length ? alpha('#059669', 0.22) : '#F0E4CC',
          }}>
            <Box sx={{ flexShrink: 0, display: 'flex', color: activeKeys.length ? '#059669' : '#7A4A10' }}>
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                <circle cx="5.4" cy="10.6" r="3" />
                <path d="M7.5 8.5 13.2 2.8M11 5l1.7 1.7M9.4 6.6l1.3 1.3" />
              </svg>
            </Box>
            <Typography variant="caption" sx={{ fontSize: '0.75rem', lineHeight: 1.45, color: activeKeys.length ? '#0B6647' : '#7A4A10' }}>
              {activeKeys.length ? (
                <Trans
                  i18nKey="cibles.keys.summary"
                  count={activeKeys.length}
                  values={{ count: activeKeys.length, list: keySummaryParts.join(', ') }}
                  components={{ b: <Box component="span" sx={{ fontWeight: 700 }} /> }}
                />
              ) : t('cibles.keys.summary_none')}
            </Typography>
          </Box>
        </Box>

        {/* Tags — free-form grouping, no geo defaults (they belong to the zone, which the
            matcher already reads; a tag copied from the address only duplicates it). */}
        <Box>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontWeight: 600, fontSize: '0.75rem' }}>
            {t('cibles.tags.label')}
            {' '}
            <Typography component="span" variant="caption" sx={{ fontWeight: 400, color: 'text.disabled', fontSize: '0.75rem' }}>
              {t('cibles.optional')}
            </Typography>
          </Typography>
          <ChipListEditor
            values={tags}
            onChange={setTags}
            max={TARGET_LIMITS.maxTagsPerTarget}
            maxLength={TARGET_LIMITS.maxTagLength}
            placeholder={t('cibles.tags.placeholder')}
            suggestions={[...tagRegistry.values()]}
            disabled={saving}
            hex="#2563EB"
          />
          <Typography variant="caption" sx={{ mt: 0.3, fontSize: '0.7rem', color: 'text.disabled', display: 'block' }}>
            {t('cibles.tags.hint')}
          </Typography>
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, pt: 0.5 }}>
          <Button variant="contained" disableElevation onClick={submit} disabled={!canSubmit}
            sx={{ borderRadius: 1.5, bgcolor: '#059669', '&:hover': { bgcolor: '#0B6647' }, fontSize: '0.875rem', fontWeight: 600 }}>
            {saving ? <CircularProgress size={16} color="inherit" /> : t('cibles.actions.add')}
          </Button>
          <Button variant="text" onClick={onClose} disabled={saving}
            sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            {t('cibles.actions.cancel')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
